import { Achievement, Entity, Nullable } from '@hglobal/common';
import { BusinessUnit } from '@pt/models';

export interface UserGroup extends Entity {
  name: string;
}

export interface User extends Entity {
  fullName: string;
  login?: string;
}

export interface UserProfile extends User {
  imageUrl: string;
  position: string;
  email: string;
}

export interface OrderUser extends User {
  phone: string;
}

export interface BalanceOrderUser extends UserProfile, OrderUser {
  location: string;
  balance: number;
}

export interface UserProfileWithJiraKey extends UserProfile {
  jiraKey: string;
}

export interface Author extends UserProfile {
  subscribesCount: number;
  isSubscribed: boolean;
}

export interface Newcomer extends UserProfile {
  questionnaireImageUrl: Nullable<string>;
}

export interface Manager extends User {
  picture: string;
  position: string;
  email: string;
}

export interface Managers {
  directManager: Manager | null;
  functionalManager: Manager | null;
  peoplePartner: Manager | null;
}

export interface MainUserInfo {
  email: string;
  location: string;
  locationId: number;
  birthday: string;
  team: string;
  segment?: string;
  service?: string;
  function?: string;
  personalYearStart: string;
  personalYearEnd: string;
}

export interface Colleague
  extends User,
  Managers,
  UserSlackId,
  UserVacationInfo,
  NotifyOnBday,
  Omit<MainUserInfo, 'personalYearStart' | 'personalYearEnd' | 'businessUnit'> {
  achievements: Achievement[];
  hobby: string;
  picture: string;
  position: string;
  startDate: string;
}

export interface Colleagues {
  direct: UserProfile[] | null;
  functional: UserProfile[] | null;
}

export interface PersonalInfoMainInfo extends MainUserInfo {
  status: string;
  businessUnit: string;
}

export interface PersonalInfo extends User, PersonalInfoMainInfo, Managers {
  position: string;
  hobby: string;
  startDate: string;
  picture: string;
  phone: string;
  myGoals: string;
  vacationAvailablePaid: number;
  updated: string;
  balance: number;
  is2faEnabled: boolean;
  colleagues: Colleagues;
  teamUsers: Colleagues;
  age: string;
  workFormat: string;
  gender: string;
  seniority: string;
  businessUnit: string;
  restrictions: string[];
  isMentoringAssigned: boolean;
  staffType: string;
  staffProject: string;
  jiraId?: number;
}

export interface PersonOfficeVisit {
  month: number;
  year: number;
  workingDays: number;
  holidayDays: number;
  absenceDays: number;
  expectedInOfficeDays: number;
  officeDays: number;
}

export interface SigninWithCodeData {
  token: string;
  is2faEnabled: boolean;
}

export interface Enable2FAData {
  qrCode: string;
  manualSetupUri: string;
  recoveryCodes: string[];
}

export enum UserRoles {
  User = 'user',
  Admin = 'admin',
}

export type UserStatusType = 'active' | 'probation' | 'offboarding';

export interface UserAdmin extends User {
  email: string;
  status: UserStatusType;
  balance: number;
  functionalBalance: number | null;
  cabinetId?: number;
  isAdmin?: boolean;
  twoFa?: boolean;
  accessGroups?: string[];
}

export interface UserGoogle {
  email: string;
  first_name: string;
  google_id: string;
  groups: string[];
  is_2fa_enabled: boolean;
  last_login: string;
  last_name: string;
  picture: string;
}

export interface UserInfoMainInfo extends MainUserInfo {
  status: UserStatusType;
}

export interface UserGroups {
  groups: UserGroup[];
}

export interface AdditionalInfo {
  hobby: string;
  phone: string;
  startDate: string;
}

export interface UserInfoAdmin extends User, AdditionalInfo, UserInfoMainInfo, Managers, UserGroups,
  UserSlackId {
  position: string;
  balance: number;
  picture: string;
  achievements: Achievement[];
}

export interface AccessData {
  refresh: string;
  access: string;
  permissions: string[];
}

export type DevicePlatform = 'desktop' | 'mobile' | 'tablet';

export interface DeviceCredential extends Entity {
  name: string;
  signCount: number;
  createdAt: string;
  platform: DevicePlatform;
}

export interface NotifyOnBday {
  notifyOnBday: boolean;
}

export interface UserSlackId {
  slackId: string;
}

export interface UserVacationInfo {
  vacation: {
    details: string; // TODO: Can be html-like string
    expiredAt: string;
  } | null;
}

export interface UserOrgTree extends Partial<UserSlackId>, UserProfile {
  locationName: string;
  segment?: string;
  service?: string;
  function?: string;
  techTeam?: string;
  team?: string;
  businessUnitId?: number;
  directManagerId: number | null;
  functionalManagerId: number | null;
  isRoot?: boolean;
  isActive: boolean;
  businessUnit?: BusinessUnit;
}

export interface BirthdayUser extends UserProfile, NotifyOnBday {
}

export type PersonalInfoAdditionalInfo = Pick<PersonalInfo, 'phone' | 'startDate' | 'hobby'>;
export type PersonalInfoRest = Omit<
  PersonalInfo, keyof PersonalInfoMainInfo | keyof PersonalInfoAdditionalInfo | keyof Managers
>;

export interface UserTelegramHistory {
  id: number;
  telegramUsername: string;
  telegramChatUsername: string;
  action: string;
  managerJiraId: number;
  createdAt: string;
}
